import { useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client/react";
import { gql } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { ColumnDef } from "@tanstack/react-table";
import { Link, useNavigate } from "react-router-dom";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";

import { TablePaginate } from "../../../components/table";
import { useNotifyContext, NotifyType } from "../../../contexts/NotifyContext";
import { AlertModal, AlertType, useAlert } from "../../../hooks/useAlert";

import { Button } from "../../../components/form";
import { AuthShield } from "../../auth/core";
import { Head } from "../../../components/core";

type Vendor = {
  id: string;
  companyName: string;
  email: string;
  phoneNumber: string;
  vendorCode: string;
  webAddress: string;
  vendorType: number;
  address: string;
  comments: string;
  createdAt: string;
  status: boolean;
};

const FETCH_VENDORS = gql`
  query FetchVendors {
    fetchVendors {
      id
      companyName
      email
      phoneNumber
      vendorCode
      webAddress
      vendorType
      address
      comments
      createdAt
      status
    }
  }
`;

const VendorList = ({ breadcrumbs }: { breadcrumbs: Breadcrumb[] }) => {
  const { addNotify } = useNotifyContext();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [fetchVendors, { loading }] = useLazyQuery(FETCH_VENDORS);
  const [vendors, setVendors] = useState<Vendor[]>([]);

  useEffect(() => {
    fetchVendors()
      .then(({ data, error }) => {
        if (data?.fetchVendors) {
          const updatedVendors = data.fetchVendors.map((item: Vendor) => ({
            ...item,
            createdAt: DateTime.fromISO(item.createdAt, {
              locale: i18n.language,
            }).toLocaleString(DateTime.DATE_MED),
          }));
          setVendors(updatedVendors);
        } else {
          return navigate("/error/401", {
            state: {
              message: error
                ? error.message
                : "Vendor read permission(s) is required to access this page.",
            },
          });
        }
      })
      .catch((error) => {
        return navigate("/error/500", {
          state: {
            error,
            message: error.message,
          },
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchVendors]);

  const DELETE_VENDOR = gql`
    mutation VendorDelete($id: ID!) {
      vendorDelete(input: { id: $id }) {
        vendor {
          id
          companyName
        }
      }
    }
  `;
  const [deleteVendor] = useMutation(DELETE_VENDOR);
  const handleDelete = async (id: string) => {
    return await deleteVendor({
      variables: {
        id,
      },
    })
      .then(({ data }) => {
        if (data?.vendorDelete) {
          const deletedVendor = data?.vendorDelete?.vendor;
          const newVendors = [...vendors];
          const index = newVendors.findIndex((vendor) => vendor.id === id);
          newVendors.splice(index, 1);
          setVendors(newVendors);
          addNotify({
            type: NotifyType.SUCCESS,
            title: `Vendor ${deletedVendor.companyName} deleted`,
          });
        } else {
          addNotify({
            type: NotifyType.ERROR,
            title: "Vendor delete failed",
            message: "Something went wrong, please try again later",
          });
        }
      })
      .catch((error) => {
        addNotify({
          type: NotifyType.ERROR,
          title: "Vendor delete failed",
          message: error.message,
        });
      });
  };

  type AlertProps = {
    id: string;
    name: string;
  };
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const ResponseAlert = useAlert({
    open: alert ? true : false,
    title: "Do you want to proceed with this operation?",
    message: (
      <p className="text-sm text-gray-500">
        Are you sure you wish to delete{" "}
        <b className="text-gray-900">{alert?.name}</b> permanently. This action
        can't be undone. All records related to{" "}
        <b className="text-gray-900">{alert?.name}</b> will be deleted.
      </p>
    ),
    type: AlertType.DANGER,
    modal: AlertModal.CENTERED_DOUBLE_ACTION,
    delay: 3000,
    onConfirm: async () => {
      await handleDelete(alert?.id!);
      setAlert(null);
    },
    onCancel: () => {
      setAlert(null);
    },
  });

  const columns = useMemo<ColumnDef<Vendor>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
      },
      {
        accessorKey: "vendorCode",
        header: "Code",
      },
      {
        accessorKey: "companyName",
        header: "Company",
      },
      {
        accessorKey: "vendorType",
        header: "Type",
        cell: (info) => {
          return info.getValue() === 1 ? "Company" : "Individual";
        },
      },
      {
        accessorKey: "email",
        header: "Email",
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        meta: {
          className: "w-auto md:w-20",
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (info) =>
          info.getValue() ? (
            <span className="inline-flex rounded-full bg-green-100 px-2 text-xs font-medium leading-5 text-green-800">
              Active
            </span>
          ) : (
            <span className="inline-flex rounded-full bg-red-100 px-2 text-xs font-medium leading-5 text-red-800">
              Inactive
            </span>
          ),
        meta: {
          className: "w-auto md:w-20",
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        enableSorting: false,
        enableGlobalFilter: false,
        cell: (props) => (
          <div>
            <AuthShield access={["update-vendors"]}>
              <Link
                to={`/purchases/vendors/${props.row.original.id}`}
                className="mr-4 inline-flex appearance-none py-2 text-primary-700 hover:text-primary-900"
              >
                <PencilIcon aria-hidden="true" className="text-md h-4 w-4" />
                <span className="sr-only">
                  Edit, {props.row.original.companyName}
                </span>
              </Link>
            </AuthShield>
            <AuthShield access={["destroy-vendors"]}>
              <button
                type="button"
                className="appearance-none py-2 text-red-700 hover:text-red-900"
                onClick={() => {
                  setAlert({
                    id: props.row.original.id,
                    name: props.row.original.companyName,
                  });
                }}
              >
                <TrashIcon aria-hidden="true" className="text-md h-4 w-4" />
                <span className="sr-only">
                  Delete, {props.row.original.companyName}
                </span>
              </button>
            </AuthShield>
          </div>
        ),
        meta: {
          className: "w-auto md:w-20",
        },
      },
    ],
    [i18n.language]
  );

  return (
    <>
      <Head
        title="Vendors"
        heading="Vendors"
        breadcrumbs={[
          ...breadcrumbs,
          {
            name: "Vendors",
            href: "/purchases/vendors",
          },
          {
            name: VendorListResource.name,
            href: null,
          },
        ]}
      />
      <ResponseAlert />
      <div className="rounded-xl bg-greyish p-5">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-xl font-medium text-gray-900">
              Vendors List
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              A list of all the vendors in your account including their name,
              title, email and vendor.
            </p>
          </div>
        </div>
        <TablePaginate
          data={vendors}
          columns={columns}
          loading={loading}
          className="mt-4"
          renderSubComponent={() => (
            <AuthShield access={["create-vendors"]}>
              <Link to="/purchases/vendors/add">
                <Button>Add vendor</Button>
              </Link>
            </AuthShield>
          )}
        />
      </div>
    </>
  );
};

export default VendorList;
export const VendorListResource: ResourceProps = {
  name: "Vendors List",
  description: "A list of vendors",
  access: ["read-vendors"],
  path: "vendors",
};
